import type { CSSProperties } from "react";

import { deviceTotalRows } from "@/lib/device-split";
import { fmtInt, fmtMoney } from "@/lib/fmt";
import type { SearchTermDeviceRow } from "@/lib/types";

/**
 * Footer of the Search terms grid with «Split by devices» on: Total filtered
 * over every (term × country) of the listing — all pages, not the one on
 * screen — and its Total for iPhone / Total for iPad lines. The grid shows
 * only four sums (impressions, taps, installs, spend), so there is nothing to
 * recompute; geo, source and the app-wide share-of-voice columns have no
 * total and show "—". Without the split the grid has no footer at all.
 *
 * Column order follows the grid: checkbox, term, [translation], geo, source,
 * impressions, taps, installs, spend, SOV slot 1, SOV slot 2, SOV total,
 * rank, popularity.
 */
export function SearchTermsSplitFooter({
  count,
  totals,
  deviceTotals,
  showTranslation,
}: {
  count: number;
  totals: SearchTermDeviceRow;
  deviceTotals: Partial<Record<string, SearchTermDeviceRow | null>> | null | undefined;
  showTranslation: boolean;
}) {
  const off = (n: number) => ({ "--foot-off": n }) as CSSProperties;
  const lines = deviceTotalRows(deviceTotals);
  return (
    <tfoot>
      <tr className="row-total foot-stack" style={off(lines.length)}>
        <td />
        <td>
          <span className="total-lbl">Total filtered search terms</span>
          <span className="muted" style={{ marginLeft: 8, fontSize: 11.5 }}>
            {fmtInt(count)}
          </span>
        </td>
        <Cells data={totals} showTranslation={showTranslation} />
      </tr>
      {lines.map((d, i) => (
        <tr
          key={d.device}
          className="dev-sub dev-sub-total foot-stack"
          style={off(lines.length - 1 - i)}
        >
          <td />
          <td>
            <span className="dev-sub-label">{d.label}</span>
          </td>
          <Cells data={d.data} showTranslation={showTranslation} />
        </tr>
      ))}
    </tfoot>
  );
}

function Cells({
  data,
  showTranslation,
}: {
  data: SearchTermDeviceRow | null;
  showTranslation: boolean;
}) {
  return (
    <>
      {showTranslation && <td />}
      <td>—</td>
      <td>—</td>
      <td className="num">{data ? fmtInt(data.impressions) : "—"}</td>
      <td className="num">{data ? fmtInt(data.taps) : "—"}</td>
      <td className="num">{data ? fmtInt(data.installs) : "—"}</td>
      <td className="num money">
        {data ? `$${fmtMoney(parseFloat(data.spend_usd))}` : "—"}
      </td>
      <td className="num">—</td>
      <td className="num">—</td>
      <td className="num">—</td>
      <td className="num">—</td>
      <td className="num">—</td>
    </>
  );
}
